/**
 * coilParallaxSilhouettes — the authored SVG parallax art for the 4 Hollow Coil
 * biomes (Coil art pack, Lane D). 12 planes (4 biomes × far/mid/near), replacing
 * the seeded-random polygon profiles with art-directed silhouettes.
 *
 * Design sources (read before editing):
 *   docs/design/coil-art/STYLE_GUIDE.md      — shape language per tier
 *   docs/design/coil-art/COLOR_PALETTE.md    — every hex code (don't invent)
 *   docs/design/coil-art/COIL_LIGHT_SPEC.md  — THE through-line: the lighthouse
 *
 * The Coil Light appears as ONE shared path (`COIL_LIGHT_PATH`) so the pig_beach
 * far spire and the brackenmoor looming tower are literally the same structure —
 * the spiral twist, lantern room, and 5-spike crown stay recognizable at every
 * distance. Tidewend deliberately has NO lighthouse (the village faces the sea).
 * The undercroft's columns ARE the lighthouse interior.
 *
 * All silhouettes are solid fills — no stroke, no interior detail (background
 * shapes). Tiles are authored with matching left/right profile-edge heights so
 * horizontal repeats seam invisibly. Corruption glow is always #00ff7f
 * (CORRUPTION_GREEN) at the opacities COIL_LIGHT_SPEC prescribes.
 */
import type { ParallaxSvgSpec } from '../../../../config/atmosphere/effectLayer.types';

/** The corruption green — the one colour that ties the whole arc together. */
export const CORRUPTION_GREEN = 0x00ff7f;

// ---------------------------------------------------------------------------
// The Coil Light — the spiral lighthouse (shared by pig_beach + brackenmoor).
// ViewBox 400×1000, baseline y=1000, centred on x≈200. Reading the outline
// bottom-left → up the twisting left edge → lantern room flare → 5-spike crown
// (centre tallest) → down the right edge (curvature out of phase with the left:
// that phase offset IS the corkscrew) → broad stepped base.
// ---------------------------------------------------------------------------
export const COIL_LIGHT_VIEWBOX = { width: 400, height: 1000 };

export const COIL_LIGHT_PATH = [
  'M55,1000 L110,905 L128,860',                    // broad stepped base, left
  'C148,800 138,750 156,690',                      // lower tower — twist eases in
  'C172,635 150,585 162,530',                      // mid tower — twist most pronounced
  'C176,478 158,432 172,385',
  'L168,330',                                      // tower top below the lantern
  'L140,318 L136,255',                             // lantern room flare, left wall
  'L128,238',                                      // crown ledge
  'L122,150 L140,232',                             // outer-left spike
  'L150,225 L152,105 L166,225',                    // mid-left spike
  'L188,215 L200,20 L212,215',                     // centre spike — the dark light
  'L234,225 L248,105 L260,225',                    // mid-right spike
  'L270,232 L278,150 L272,238',                    // outer-right spike
  'L264,255 L260,318 L232,330',                    // lantern room, right wall
  'L228,385 C242,432 224,478 238,530',             // right edge — opposite phase
  'C250,585 228,635 244,690',
  'C262,750 252,800 272,860',
  'L290,905 L345,1000 Z',                          // broad stepped base, right
].join(' ');

/** Faint halo around the (dark) lantern room — the corruption visible from the
 *  moor. An ellipse approximated with cubics, centred on the lantern (200,240). */
const COIL_LIGHT_LANTERN_GLOW =
  'M90,240 C90,174 139,120 200,120 C261,120 310,174 310,240 ' +
  'C310,306 261,360 200,360 C139,360 90,306 90,240 Z';

// ---------------------------------------------------------------------------
// Small silhouette generators (pure string builders, run once at module init).
// Keeping repeated props (palms, dead trees, bracken, pylons, columns) as
// parametric helpers keeps the authored tiles readable + consistent.
// ---------------------------------------------------------------------------

/** Palm silhouette — curved trunk + 5 frond blades. Crown at (cx, g-200). */
function palm(cx: number, g: number): string {
  const cy = g - 200;
  return [
    // trunk (gentle lean)
    `M${cx - 12},${g} C${cx - 16},${g - 80} ${cx - 20},${g - 140} ${cx - 2},${g - 196} ` +
    `L${cx + 10},${g - 192} C${cx - 6},${g - 135} ${cx - 2},${g - 80} ${cx + 6},${g} Z`,
    // fronds — left, upper-left, top, upper-right, right
    `M${cx},${cy} C${cx - 40},${cy - 28} ${cx - 90},${cy - 36} ${cx - 140},${cy - 22} C${cx - 90},${cy - 12} ${cx - 42},${cy - 2} ${cx - 2},${cy + 10} Z`,
    `M${cx},${cy} C${cx - 30},${cy - 45} ${cx - 62},${cy - 72} ${cx - 100},${cy - 86} C${cx - 58},${cy - 58} ${cx - 26},${cy - 30} ${cx + 4},${cy + 2} Z`,
    `M${cx},${cy} C${cx + 8},${cy - 55} ${cx + 4},${cy - 95} ${cx - 10},${cy - 130} C${cx + 16},${cy - 88} ${cx + 20},${cy - 42} ${cx + 12},${cy + 2} Z`,
    `M${cx},${cy} C${cx + 34},${cy - 44} ${cx + 70},${cy - 64} ${cx + 114},${cy - 70} C${cx + 70},${cy - 42} ${cx + 36},${cy - 18} ${cx + 6},${cy + 8} Z`,
    `M${cx},${cy} C${cx + 44},${cy - 20} ${cx + 94},${cy - 18} ${cx + 140},${cy} C${cx + 94},${cy - 6} ${cx + 46},${cy + 4} ${cx + 4},${cy + 12} Z`,
  ].join(' ');
}

/** Bare dead tree — trunk with three gnarled forks (Tier-2 "the turn"). */
function deadTree(bx: number, g: number): string {
  return (
    `M${bx - 10},${g} L${bx - 4},${g - 110} ` +
    `L${bx - 22},${g - 150} L${bx - 16},${g - 156} L${bx},${g - 122} ` +
    `L${bx + 2},${g - 170} ` +
    `L${bx - 10},${g - 200} L${bx - 3},${g - 204} L${bx + 8},${g - 176} ` +
    `L${bx + 14},${g - 212} L${bx + 22},${g - 208} L${bx + 14},${g - 168} ` +
    `L${bx + 30},${g - 190} L${bx + 35},${g - 184} L${bx + 16},${g - 148} ` +
    `L${bx + 10},${g - 110} L${bx + 18},${g} Z`
  );
}

/** Jagged dead-bracken tuft — sharp W of blades (Tier-2/3 shape language). */
function brackenTuft(bx: number, g: number): string {
  return (
    `M${bx},${g} L${bx + 12},${g - 330} L${bx + 26},${g - 90} L${bx + 42},${g - 370} ` +
    `L${bx + 56},${g - 110} L${bx + 72},${g - 280} L${bx + 84},${g} Z`
  );
}

/** Dock pylon pair + tilted crossbeam (tidewend near foreground). */
function dockPylons(bx: number, g: number): string {
  return [
    `M${bx},${g} L${bx + 6},${g - 480} L${bx + 36},${g - 480} L${bx + 42},${g} Z`,
    `M${bx + 80},${g + 10} L${bx + 88},${g - 420} L${bx + 114},${g - 420} L${bx + 120},${g + 10} Z`,
    `M${bx - 20},${g - 380} L${bx + 140},${g - 400} L${bx + 144},${g - 370} L${bx - 16},${g - 348} Z`,
  ].join(' ');
}

/** Undercroft interior column — full-bleed (y 0..1000) with a curved lean:
 *  the columns are not vertical; the slight bow suggests the inner spiral. */
function coilColumn(x0: number, w: number, lean: number): string {
  return (
    `M${x0},1000 C${x0 + lean * 0.15},700 ${x0 + lean * 0.5},350 ${x0 + lean},0 ` +
    `L${x0 + w + lean},0 C${x0 + w + lean * 0.5},350 ${x0 + w + lean * 0.15},700 ${x0 + w},1000 Z`
  );
}

/** Vertical corruption-glow shaft in a column gap (light seeping up the seam). */
function glowShaft(x: number, topY: number, w: number): string {
  return `M${x},1000 L${x + w * 0.25},${topY} L${x + w * 0.75},${topY} L${x + w},1000 Z`;
}

/** Thin glowing fissure crawling up a column face. */
function glowCrack(cx: number, topY: number, botY: number): string {
  const mid = (topY + botY) / 2;
  return (
    `M${cx},${botY} L${cx + 14},${mid + 60} L${cx + 4},${mid} L${cx + 22},${topY + 40} ` +
    `L${cx + 28},${topY} L${cx + 37},${topY + 6} L${cx + 32},${topY + 52} ` +
    `L${cx + 14},${mid + 8} L${cx + 24},${mid + 66} L${cx + 10},${botY} Z`
  );
}

// ---------------------------------------------------------------------------
// pig_beach — "Sunny Arrival Cove". Tier 1: round, soft, forgiving. The ONLY
// cold note is the far dark spire of the Coil Light on the horizon.
// ---------------------------------------------------------------------------

export const PIG_BEACH_SILHOUETTES: { far: ParallaxSvgSpec; mid: ParallaxSvgSpec; near: ParallaxSvgSpec } = {
  far: {
    // Soft green headland rolling along the horizon; edges meet at y=700.
    tile: {
      path:
        'M0,1000 L0,700 ' +
        'C150,640 300,560 520,560 C700,560 820,660 1000,680 ' +
        'C1200,700 1350,540 1560,520 C1760,505 1900,620 2080,660 ' +
        'C2260,700 2450,740 2650,730 C2850,720 2950,600 3150,570 ' +
        'C3350,545 3600,640 3800,690 C3900,712 3960,706 4000,700 ' +
        'L4000,1000 Z',
      viewBoxWidth: 4000,
      viewBoxHeight: 1000,
    },
    // THE FIRST APPEARANCE: a thin cool-dark spire rising from behind the
    // headland — ~82px (≈11% of a 720p view), deliberately colder (#3a4a3a)
    // than everything around it. No glow: from here it's just… dark.
    // liftPx floats the spire above the mid ridge crests (~130px) while keeping
    // the tip inside the sky strip a typical camera shows (~200px above the
    // horizon) — high enough to clear the ridges, low enough to stay in frame.
    feature: {
      path: COIL_LIGHT_PATH,
      viewBoxWidth: COIL_LIGHT_VIEWBOX.width,
      viewBoxHeight: COIL_LIGHT_VIEWBOX.height,
      heightPx: 85,
      anchorX: 0.68,
      liftPx: 105,
      color: 0x3a4a3a,
      alpha: 0.85,
      order: 'behindTile',
    },
  },
  mid: {
    // Darker headland ridge with three palms leaning over the crest.
    tile: {
      path: [
        'M0,1000 L0,620 ' +
          'C200,560 380,500 620,510 C860,520 1000,640 1240,660 ' +
          'C1480,680 1650,560 1900,540 C2150,520 2350,620 2600,650 ' +
          'C2850,680 3100,600 3350,560 C3600,525 3820,580 4000,620 ' +
          'L4000,1000 Z',
        palm(700, 560),
        palm(2050, 600),
        palm(3300, 610),
      ].join(' '),
      viewBoxWidth: 4000,
      viewBoxHeight: 1000,
    },
  },
  near: {
    // Low warm sand-dune ridge — gentle, harmless.
    tile: {
      path:
        'M0,1000 L0,620 ' +
        'C300,540 700,500 1100,560 C1500,620 1800,720 2200,700 ' +
        'C2600,680 2900,560 3300,540 C3600,525 3850,570 4000,620 ' +
        'L4000,1000 Z',
      viewBoxWidth: 4000,
      viewBoxHeight: 1000,
    },
  },
};

// ---------------------------------------------------------------------------
// tidewend — "Cozy Village at Dusk". Clustered gabled rooftops + round tree
// canopies huddled against the sea. NO lighthouse anywhere — the village has
// its back to the inland dark; the absence is the point.
// ---------------------------------------------------------------------------

export const TIDEWEND_SILHOUETTES: { far: ParallaxSvgSpec; mid: ParallaxSvgSpec; near: ParallaxSvgSpec } = {
  far: {
    // Village skyline: five gables (two with chimneys) nestled between soft
    // round canopies; edges meet at y=720.
    tile: {
      path:
        'M0,1000 L0,720 ' +
        'C100,700 180,690 260,690 ' +
        'L320,690 L420,560 L520,690 ' +                       // house 1
        'L560,690 L566,600 L596,600 L602,690 ' +              // chimney
        'L700,690 ' +
        'C760,610 860,580 940,640 C990,675 1040,690 1100,690 ' + // tree
        'L1180,690 L1300,520 L1420,690 ' +                    // house 2 (tall)
        'L1500,690 L1600,580 L1700,690 ' +                    // house 3
        'C1780,640 1900,600 2000,660 C2080,700 2150,710 2250,710 ' + // tree
        'L2350,710 L2470,560 L2590,710 ' +                    // house 4
        'L2640,710 L2646,610 L2678,610 L2684,710 ' +          // chimney
        'L2790,710 ' +
        'C2900,600 3050,560 3180,630 C3260,672 3340,690 3420,690 ' + // big tree
        'L3520,690 L3620,590 L3720,690 ' +                    // house 5 (small)
        'C3800,700 3900,712 4000,720 ' +
        'L4000,1000 Z',
      viewBoxWidth: 4000,
      viewBoxHeight: 1000,
    },
  },
  mid: {
    // Closer, darker roofline — bigger gables, one chimney, tree ridges.
    tile: {
      path:
        'M0,1000 L0,640 ' +
        'L140,640 L320,400 L500,640 ' +
        'L560,640 L570,500 L610,500 L620,640 ' +
        'L760,640 ' +
        'C880,520 1040,480 1180,560 C1260,605 1340,630 1440,630 ' +
        'L1560,630 L1780,360 L2000,630 ' +
        'L2120,630 L2260,470 L2400,630 ' +
        'C2520,560 2660,540 2780,600 C2860,640 2960,650 3060,650 ' +
        'L3180,650 L3380,420 L3580,650 ' +
        'C3720,655 3860,650 4000,640 ' +
        'L4000,1000 Z',
      viewBoxWidth: 4000,
      viewBoxHeight: 1000,
    },
  },
  near: {
    // Darkest foreground strip: the shore line with dock pylons + a lone
    // mooring post — the village lives on the water.
    tile: {
      path: [
        'M0,1000 L0,780 ' +
          'C400,760 800,770 1200,780 C1800,795 2400,795 3000,780 ' +
          'C3400,770 3700,770 4000,780 ' +
          'L4000,1000 Z',
        dockPylons(880, 800),
        `M1890,795 L1896,520 L1920,520 L1926,795 Z`, // lone mooring post
        dockPylons(2880, 800),
      ].join(' '),
      viewBoxWidth: 4000,
      viewBoxHeight: 1000,
    },
  },
};

// ---------------------------------------------------------------------------
// brackenmoor — "The Path Greys Out". Tier 2: rolling drained moor, dead trees
// on the ridgeline, jagged bracken up close — and the Coil Light LOOMING in
// the near plane, lantern haloed faintly corruption-green. The turn.
// ---------------------------------------------------------------------------

export const BRACKENMOOR_SILHOUETTES: { far: ParallaxSvgSpec; mid: ParallaxSvgSpec; near: ParallaxSvgSpec } = {
  far: {
    // Long low moor swells; two dead trees break the far ridgeline.
    tile: {
      path: [
        'M0,1000 L0,680 ' +
          'C300,620 600,540 1000,560 C1400,580 1700,700 2100,720 ' +
          'C2500,740 2800,600 3200,560 C3500,530 3800,620 4000,680 ' +
          'L4000,1000 Z',
        deadTree(1000, 580),
        deadTree(3200, 580),
      ].join(' '),
      viewBoxWidth: 4000,
      viewBoxHeight: 1000,
    },
  },
  mid: {
    // Darker swells, one more dead tree — the moor closes in.
    tile: {
      path: [
        'M0,1000 L0,620 ' +
          'C350,560 700,460 1150,480 C1550,498 1850,640 2250,660 ' +
          'C2650,680 2950,520 3350,480 C3650,452 3850,540 4000,620 ' +
          'L4000,1000 Z',
        deadTree(1150, 500),
      ].join(' '),
      viewBoxWidth: 4000,
      viewBoxHeight: 1000,
    },
  },
  near: {
    // Dead-bracken foreground ridge + THE COIL LIGHT LOOMING — same structure
    // the player saw from the beach, now ~260px (≈36% of a 720p view), nearly
    // black, its dark lantern haloed by the corruption at 10%.
    tile: {
      path: [
        'M0,1000 L0,720 ' +
          'C500,700 1000,710 1500,720 C2200,732 3000,725 4000,720 ' +
          'L4000,1000 Z',
        brackenTuft(300, 740),
        brackenTuft(1300, 735),
        brackenTuft(2300, 745),
        brackenTuft(3400, 738),
      ].join(' '),
      viewBoxWidth: 4000,
      viewBoxHeight: 1000,
    },
    feature: {
      path: COIL_LIGHT_PATH,
      viewBoxWidth: COIL_LIGHT_VIEWBOX.width,
      viewBoxHeight: COIL_LIGHT_VIEWBOX.height,
      heightPx: 260,
      anchorX: 0.63,
      color: 0x1a2a1a,
      order: 'inFrontOfTile',
      glowPath: COIL_LIGHT_LANTERN_GLOW,
      glowColor: CORRUPTION_GREEN,
      glowAlpha: 0.1,
    },
  },
};

// ---------------------------------------------------------------------------
// coil_undercroft — "The Dark Heart". You are INSIDE the Coil Light: the
// columns are the spiral's walls (each bows slightly — the corkscrew read),
// corruption green seeping through gap seams and fissures. Far = many thin
// columns; near = 2 massive near-black walls. Enclosed, descending.
// ---------------------------------------------------------------------------

export const COIL_UNDERCROFT_SILHOUETTES: { far: ParallaxSvgSpec; mid: ParallaxSvgSpec; near: ParallaxSvgSpec } = {
  far: {
    // Eight thin columns, alternating lean — the far wall of the spiral.
    tile: {
      path: [
        coilColumn(40, 80, 25), coilColumn(290, 80, -25),
        coilColumn(540, 80, 25), coilColumn(790, 80, -25),
        coilColumn(1040, 80, 25), coilColumn(1290, 80, -25),
        coilColumn(1540, 80, 25), coilColumn(1790, 80, -25),
      ].join(' '),
      viewBoxWidth: 2000,
      viewBoxHeight: 1000,
      glowPath: [
        glowShaft(190, 140, 60),
        glowShaft(940, 200, 55),
        glowShaft(1690, 160, 60),
      ].join(' '),
      glowColor: CORRUPTION_GREEN,
      glowAlpha: 0.08,
    },
  },
  mid: {
    // Five wider columns — the spiral tightens; a fissure glows on one face.
    tile: {
      path: [
        coilColumn(60, 160, 35), coilColumn(460, 160, -35),
        coilColumn(860, 160, 35), coilColumn(1260, 160, -35),
        coilColumn(1660, 160, 35),
      ].join(' '),
      viewBoxWidth: 2000,
      viewBoxHeight: 1000,
      glowPath: [
        glowShaft(280, 240, 70),
        glowShaft(1480, 280, 70),
        glowCrack(520, 380, 700),
      ].join(' '),
      glowColor: CORRUPTION_GREEN,
      glowAlpha: 0.13,
    },
  },
  near: {
    // Two massive near-black walls — you're deep inside; the green in the
    // seams is the only light (COIL_LIGHT_SPEC: 20%).
    tile: {
      path: [
        coilColumn(120, 380, 45),
        coilColumn(1120, 380, -45),
      ].join(' '),
      viewBoxWidth: 2000,
      viewBoxHeight: 1000,
      glowPath: [
        glowShaft(700, 320, 90),
        glowCrack(260, 300, 720),
        glowCrack(1330, 260, 680),
      ].join(' '),
      glowColor: CORRUPTION_GREEN,
      glowAlpha: 0.18,
    },
  },
};
